<template>
  <div class="style-config">
    <ConfigSection title="字体设置" icon="🔤">
      <div class="form-group">
        <label class="form-label">默认字体</label>
        <select v-model="defaultFont" class="form-control">
          <option value="微软雅黑">微软雅黑</option>
          <option value="宋体">宋体</option>
          <option value="黑体">黑体</option>
          <option value="楷体">楷体</option>
          <option value="Arial">Arial</option>
          <option value="Times New Roman">Times New Roman</option>
        </select>
      </div>
      
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">默认字号</label>
          <select v-model="defaultFontSize" class="form-control">
            <option value="10">10pt</option>
            <option value="12">12pt</option>
            <option value="14">14pt</option>
            <option value="16">16pt</option>
            <option value="18">18pt</option>
            <option value="20">20pt</option>
            <option value="24">24pt</option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-label">行高</label>
          <select v-model="lineHeight" class="form-control">
            <option value="1.2">1.2倍</option>
            <option value="1.4">1.4倍</option>
            <option value="1.5">1.5倍</option>
            <option value="1.6">1.6倍</option>
            <option value="2.0">2.0倍</option>
          </select>
        </div>
      </div>
    </ConfigSection>

    <ConfigSection title="表格样式" icon="📊">
      <div class="form-group">
        <label class="form-label">表格边框</label>
        <div class="border-controls">
          <select v-model="tableBorderStyle" class="form-control">
            <option value="solid">实线</option>
            <option value="dashed">虚线</option>
            <option value="dotted">点线</option>
            <option value="double">双线</option>
          </select>
          <input
            v-model.number="tableBorderWidth"
            type="number"
            min="0"
            max="10"
            class="form-control"
            style="width: 80px;"
          />
          <span class="unit">px</span>
        </div>
      </div>
      
      <div class="form-group">
        <label class="form-label">边框颜色</label>
        <div class="color-picker">
          <input
            v-model="tableBorderColor"
            type="color"
            class="color-input"
          />
          <input
            v-model="tableBorderColor"
            type="text"
            class="form-control"
            placeholder="#000000"
          />
        </div>
      </div>
      
      <div class="form-group">
        <label class="form-label">表头样式</label>
        <div class="checkbox-group">
          <label class="checkbox-item">
            <input v-model="tableHeaderBold" type="checkbox" />
            <span class="checkbox-label">加粗表头</span>
          </label>
          <label class="checkbox-item">
            <input v-model="tableHeaderBackground" type="checkbox" />
            <span class="checkbox-label">表头背景色</span>
          </label>
        </div>
      </div>
      
      <div v-if="tableHeaderBackground" class="form-group">
        <label class="form-label">表头背景色</label>
        <div class="color-picker">
          <input
            v-model="tableHeaderBgColor"
            type="color"
            class="color-input"
          />
          <input
            v-model="tableHeaderBgColor"
            type="text"
            class="form-control"
            placeholder="#f8f9fa"
          />
        </div>
      </div>
    </ConfigSection>

    <ConfigSection title="颜色主题" icon="🎨">
      <div class="theme-selector">
        <div class="theme-grid">
          <div
            v-for="theme in themes"
            :key="theme.name"
            @click="applyTheme(theme)"
            :class="['theme-item', { active: currentTheme === theme.name }]"
          >
            <div class="theme-preview">
              <div 
                class="color-swatch primary"
                :style="{ backgroundColor: theme.primary }"
              ></div>
              <div 
                class="color-swatch secondary"
                :style="{ backgroundColor: theme.secondary }"
              ></div>
              <div 
                class="color-swatch accent"
                :style="{ backgroundColor: theme.accent }"
              ></div>
            </div>
            <span class="theme-name">{{ theme.name }}</span>
          </div>
        </div>
      </div>
      
      <div class="custom-colors">
        <h5>自定义颜色</h5>
        <div class="color-row">
          <div class="color-group">
            <label class="color-label">主色调</label>
            <div class="color-picker">
              <input
                v-model="primaryColor"
                type="color"
                class="color-input"
              />
              <input
                v-model="primaryColor"
                type="text"
                class="form-control"
              />
            </div>
          </div>
          <div class="color-group">
            <label class="color-label">辅助色</label>
            <div class="color-picker">
              <input
                v-model="secondaryColor"
                type="color"
                class="color-input"
              />
              <input
                v-model="secondaryColor"
                type="text"
                class="form-control"
              />
            </div>
          </div>
        </div>
      </div>
    </ConfigSection>

    <ConfigSection title="背景设置" icon="🖼️">
      <div class="form-group">
        <label class="form-label">背景类型</label>
        <div class="radio-group">
          <label class="radio-item">
            <input v-model="backgroundType" type="radio" value="none" />
            <span class="radio-label">无背景</span>
          </label>
          <label class="radio-item">
            <input v-model="backgroundType" type="radio" value="color" />
            <span class="radio-label">纯色</span>
          </label>
          <label class="radio-item">
            <input v-model="backgroundType" type="radio" value="image" />
            <span class="radio-label">图片</span>
          </label>
        </div>
      </div>
      
      <div v-if="backgroundType === 'color'" class="form-group">
        <label class="form-label">背景颜色</label>
        <div class="color-picker">
          <input
            v-model="backgroundColor"
            type="color"
            class="color-input"
          />
          <input
            v-model="backgroundColor"
            type="text"
            class="form-control"
            placeholder="#ffffff"
          />
        </div>
      </div>
      
      <div v-if="backgroundType === 'image'" class="form-group">
        <label class="form-label">背景图片</label>
        <div class="file-upload">
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            @change="handleImageUpload"
            style="display: none;"
          />
          <button @click="$refs.fileInput.click()" class="upload-btn">
            <span class="upload-icon">📁</span>
            选择图片
          </button>
          <span v-if="backgroundImage" class="file-name">
            {{ backgroundImageName }}
          </span>
        </div>
      </div>
    </ConfigSection>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import ConfigSection from './ConfigSection.vue'

export default {
  name: 'StyleConfig',
  components: {
    ConfigSection
  },
  
  setup() {
    const store = useStore()
    const fileInput = ref(null)
    
    // 主题预设
    const themes = [
      {
        name: '默认',
        primary: '#667eea',
        secondary: '#764ba2',
        accent: '#28a745'
      },
      {
        name: '商务',
        primary: '#2c3e50',
        secondary: '#34495e',
        accent: '#3498db'
      },
      {
        name: '清新',
        primary: '#27ae60',
        secondary: '#2ecc71',
        accent: '#f39c12'
      },
      {
        name: '温暖',
        primary: '#e74c3c',
        secondary: '#c0392b',
        accent: '#f39c12'
      }
    ]
    
    // 计算属性
    const defaultFont = computed({
      get: () => store.state.template.config.defaultFont || '微软雅黑',
      set: (value) => store.dispatch('template/updateConfig', { key: 'defaultFont', value })
    })
    
    const defaultFontSize = computed({
      get: () => store.state.template.config.defaultFontSize || 14,
      set: (value) => store.dispatch('template/updateConfig', { key: 'defaultFontSize', value: parseInt(value) })
    })
    
    const lineHeight = computed({
      get: () => store.state.template.config.lineHeight || 1.4,
      set: (value) => store.dispatch('template/updateConfig', { key: 'lineHeight', value: parseFloat(value) })
    })
    
    const tableBorderStyle = computed({
      get: () => store.state.template.config.tableBorderStyle || 'solid',
      set: (value) => store.dispatch('template/updateConfig', { key: 'tableBorderStyle', value })
    })
    
    const tableBorderWidth = computed({
      get: () => store.state.template.config.tableBorderWidth || 1,
      set: (value) => store.dispatch('template/updateConfig', { key: 'tableBorderWidth', value })
    })
    
    const tableBorderColor = computed({
      get: () => store.state.template.config.tableBorderColor || '#000000',
      set: (value) => store.dispatch('template/updateConfig', { key: 'tableBorderColor', value })
    })
    
    const tableHeaderBold = computed({
      get: () => store.state.template.config.tableHeaderBold !== false,
      set: (value) => store.dispatch('template/updateConfig', { key: 'tableHeaderBold', value })
    })
    
    const tableHeaderBackground = computed({
      get: () => store.state.template.config.tableHeaderBackground || false,
      set: (value) => store.dispatch('template/updateConfig', { key: 'tableHeaderBackground', value })
    })
    
    const tableHeaderBgColor = computed({
      get: () => store.state.template.config.tableHeaderBgColor || '#f8f9fa',
      set: (value) => store.dispatch('template/updateConfig', { key: 'tableHeaderBgColor', value })
    })
    
    const currentTheme = computed(() => store.state.template.config.theme || '默认')
    
    const primaryColor = computed({
      get: () => store.state.template.config.primaryColor || '#667eea',
      set: (value) => store.dispatch('template/updateConfig', { key: 'primaryColor', value })
    })
    
    const secondaryColor = computed({
      get: () => store.state.template.config.secondaryColor || '#764ba2',
      set: (value) => store.dispatch('template/updateConfig', { key: 'secondaryColor', value })
    })
    
    const backgroundType = computed({
      get: () => store.state.template.config.backgroundType || 'none',
      set: (value) => store.dispatch('template/updateConfig', { key: 'backgroundType', value })
    })
    
    const backgroundColor = computed({
      get: () => store.state.template.config.backgroundColor || '#ffffff',
      set: (value) => store.dispatch('template/updateConfig', { key: 'backgroundColor', value })
    })
    
    const backgroundImage = computed({
      get: () => store.state.template.config.backgroundImage || '',
      set: (value) => store.dispatch('template/updateConfig', { key: 'backgroundImage', value })
    })
    
    const backgroundImageName = computed({
      get: () => store.state.template.config.backgroundImageName || '',
      set: (value) => store.dispatch('template/updateConfig', { key: 'backgroundImageName', value })
    })
    
    // 方法
    const applyTheme = (theme) => {
      store.dispatch('template/updateConfig', { key: 'theme', value: theme.name })
      store.dispatch('template/updateConfig', { key: 'primaryColor', value: theme.primary })
      store.dispatch('template/updateConfig', { key: 'secondaryColor', value: theme.secondary })
    }
    
    const handleImageUpload = (event) => {
      const file = event.target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          backgroundImage.value = e.target.result
          backgroundImageName.value = file.name
        }
        reader.readAsDataURL(file)
      }
    }
    
    return {
      themes,
      defaultFont,
      defaultFontSize,
      lineHeight,
      tableBorderStyle,
      tableBorderWidth,
      tableBorderColor,
      tableHeaderBold,
      tableHeaderBackground,
      tableHeaderBgColor,
      currentTheme,
      primaryColor,
      secondaryColor,
      backgroundType,
      backgroundColor,
      backgroundImage,
      backgroundImageName,
      fileInput,
      applyTheme,
      handleImageUpload
    }
  }
}
</script>

<style scoped>
.style-config {
  space-y: 24px;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #495057;
  font-size: 14px;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.border-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.unit {
  font-size: 14px;
  color: #6c757d;
}

.color-picker {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-input {
  width: 50px;
  height: 40px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  cursor: pointer;
  background: none;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.checkbox-item input[type="checkbox"] {
  margin-right: 8px;
}

.checkbox-label {
  font-size: 14px;
  color: #495057;
}

.radio-group {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.radio-item {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.radio-item input[type="radio"] {
  margin-right: 8px;
}

.radio-label {
  font-size: 14px;
  color: #495057;
}

/* 主题选择器 */
.theme-selector {
  margin-bottom: 24px;
}

.theme-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.theme-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.theme-item:hover {
  border-color: #adb5bd;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.theme-item.active {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.theme-preview {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
}

.color-swatch {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid rgba(0,0,0,0.1);
}

.theme-name {
  font-size: 12px;
  color: #495057;
  font-weight: 500;
}

.custom-colors h5 {
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #495057;
}

.color-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.color-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.color-label {
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
}

/* 文件上传 */
.file-upload {
  display: flex;
  align-items: center;
  gap: 12px;
}

.upload-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #ced4da;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.upload-btn:hover {
  background: #f8f9fa;
  border-color: #adb5bd;
}

.upload-icon {
  font-size: 16px;
}

.file-name {
  font-size: 14px;
  color: #495057;
  font-style: italic;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .color-row {
    grid-template-columns: 1fr;
  }
  
  .theme-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .radio-group {
    flex-direction: column;
    gap: 8px;
  }
  
  .border-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .file-upload {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
